import { ReactNode, useEffect, useMemo, useState } from 'react';
import { SourceBadge as SharedSourceBadge } from '../../components/SourceBadge';
import { fetchCalls } from '../api';
import type { ApiCallRecord, ApiEndpointModel } from '../types';
import { ActionButton, CopyActionButton, InlineCopyButton } from './apiSnippets';
import { buildCurl } from './curl';
import { EndpointBadge } from './EndpointBadge';
import { ImageLightbox } from './ImageLightbox';

type StatusFilter = 'all' | 'success' | 'in_progress' | 'error';
type SourceFilter = 'all' | 'manual' | 'api' | 'qa';
type ViewMode = 'compact' | 'comfortable' | 'grid';

type CallSource = 'manual' | 'stress' | 'benchmark' | 'api';

/**
 * Where a call came from.
 *
 * `stress` and `benchmark` are both QA load tests, split by whether the run paid
 * for its generations. Rows written before the origin was tracked have no
 * `source`; they can only have come from a client that wasn't sending the
 * Playground header, so they count as `api`.
 */
function sourceOf(record: ApiCallRecord): CallSource {
  if (record.source === 'manual') return 'manual';
  if (record.source === 'stress') return 'stress';
  if (record.source === 'benchmark') return 'benchmark';
  return 'api';
}

const SOURCE_LABELS: Record<CallSource, string> = {
  manual: 'Manual (Playground)',
  stress: 'Stress (QA load test, dummy)',
  benchmark: 'Benchmark (QA load test, paid)',
  api: 'API',
};

function matchesSourceFilter(record: ApiCallRecord, filter: SourceFilter): boolean {
  const source = sourceOf(record);
  if (filter === 'qa') return source === 'stress' || source === 'benchmark';
  return source === filter;
}

function isInProgress(status: string): boolean {
  return status === 'pending' || status === 'processing';
}

const PAGE_SIZE = 10;
const IN_FLIGHT_POLL_MS = 3000;

interface RequestHistoryProps {
  model: ApiEndpointModel;
  /** Re-run the given request in the Playground tab (prefills prompt + params). */
  onOpenInPlayground: (record: ApiCallRecord) => void;
}

export function RequestHistory({ model, onOpenInPlayground }: RequestHistoryProps) {
  const [calls, setCalls] = useState<ApiCallRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [view, setView] = useState<ViewMode>('comfortable');
  const [showPreview, setShowPreview] = useState(true);
  // Dummy calls (stress runs, `X-Apigen-Dummy`) never reached a provider, so
  // they are noise when reading real history — off unless asked for.
  const [showDummy, setShowDummy] = useState(false);
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<ApiCallRecord | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    fetchCalls(model.key, 200)
      .then((c) => setCalls(c))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCalls(model.key, 200)
      .then((c) => {
        if (!cancelled) setCalls(c);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model.key]);

  // Keep in-flight rows fresh so a job that fails after open flips to a real
  // error badge + message without a manual Refresh.
  const hasInFlight = useMemo(
    () => (calls || []).some((r) => isInProgress(r.status)),
    [calls],
  );
  useEffect(() => {
    if (!hasInFlight) return;
    let cancelled = false;
    const id = window.setInterval(() => {
      fetchCalls(model.key, 200)
        .then((c) => {
          if (!cancelled) setCalls(c);
        })
        .catch(() => {
          // Leave the last good snapshot; the next tick can retry.
        });
    }, IN_FLIGHT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [hasInFlight, model.key]);

  // Keep the open detail sheet in sync when a polled row finishes.
  useEffect(() => {
    if (!detail || !calls) return;
    const updated = calls.find((r) => r.id === detail.id);
    if (updated && updated.status !== detail.status) {
      setDetail(updated);
    }
  }, [calls, detail]);

  // Dummy is applied last and counted separately, so the toolbar can say how
  // many rows the toggle is holding back rather than silently shortening the list.
  const matching = useMemo(() => {
    let rows = calls || [];
    if (status !== 'all') {
      rows = rows.filter((r) => {
        if (status === 'success') return r.status === 'success';
        if (status === 'error') return r.status === 'error';
        if (status === 'in_progress') return isInProgress(r.status);
        return true;
      });
    }
    if (source !== 'all') {
      rows = rows.filter((r) => matchesSourceFilter(r, source));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.id, r.job_id, r.scan_id].some((v) => v != null && v.toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [calls, status, source, search]);

  const dummyCount = useMemo(() => matching.filter((r) => r.dummy).length, [matching]);
  const filtered = useMemo(
    () => (showDummy ? matching : matching.filter((r) => !r.dummy)),
    [matching, showDummy],
  );

  // Reset to the first page whenever the filters change the result set.
  useEffect(() => {
    setPage(0);
  }, [status, source, search, showDummy, model.key]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Request History</h2>
        <div className="flex items-center gap-4">
          <label
            className="flex items-center gap-2 text-xs font-medium text-slate-500 cursor-pointer select-none"
            title="Include calls that never reached a provider (stress runs and dummy-mode requests)"
          >
            Show dummy
            <Toggle checked={showDummy} onChange={setShowDummy} />
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-500 cursor-pointer select-none">
            Show Preview
            <Toggle checked={showPreview} onChange={setShowPreview} />
          </label>
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {/* Filter card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-4 mb-5">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-3">
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 4h18M6 12h12M10 20h4" />
          </svg>
          Filter
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <label className="block flex-1 max-w-md">
            <span className="text-xs font-medium text-slate-500">Search by Request / Job / Scan ID</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Request ID, Job ID or Scan ID"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            />
          </label>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Status</span>
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                {(
                  [
                    ['all', 'All'],
                    ['success', 'Success'],
                    ['in_progress', 'In progress'],
                    ['error', 'Error'],
                  ] as [StatusFilter, string][]
                ).map(([value, label], idx) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setStatus(value)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      idx > 0 ? 'border-l border-slate-200' : ''
                    } ${
                      status === value
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Source</span>
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                {(
                  [
                    ['all', 'All'],
                    ['manual', 'Manual'],
                    ['api', 'API'],
                    ['qa', 'QA'],
                  ] as [SourceFilter, string][]
                ).map(([value, label], idx) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSource(value)}
                    title={
                      value === 'manual'
                        ? 'Requests run from the Playground'
                        : value === 'api'
                          ? 'Requests from external API clients'
                          : value === 'qa'
                            ? 'Load generated by a QA test, free (stress) or paid (benchmark)'
                            : undefined
                    }
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      idx > 0 ? 'border-l border-slate-200' : ''
                    } ${
                      source === value
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar: count + refresh + pagination */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs text-slate-400">
          {loading ? 'Loading…' : `${filtered.length} request${filtered.length === 1 ? '' : 's'}`}
          {!loading && !showDummy && dummyCount > 0 && ` · ${dummyCount} dummy hidden`}
          <button
            type="button"
            onClick={load}
            className="ml-3 inline-flex items-center gap-1 text-slate-500 hover:text-indigo-600"
            title="Refresh"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
            </svg>
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={clampedPage === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <span className="text-xs text-slate-400">
            {clampedPage + 1} / {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={clampedPage >= pageCount - 1}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          Failed to load requests: {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white/50 px-4 py-12 text-center text-sm text-slate-400">
          {!showDummy && dummyCount > 0
            ? `Every matching request for ${model.label} is a dummy call — turn on "Show dummy" to see them.`
            : `No requests recorded for ${model.label} yet.`}
        </div>
      )}

      {view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paged.map((rec) => (
            <RequestGridCard
              key={rec.id}
              record={rec}
              showPreview={showPreview}
              onOpenInPlayground={() => onOpenInPlayground(rec)}
              onDetails={() => setDetail(rec)}
            />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border-t border-slate-100">
          {paged.map((rec) => (
            <RequestRow
              key={rec.id}
              record={rec}
              compact={view === 'compact'}
              showPreview={showPreview}
              onOpenInPlayground={() => onOpenInPlayground(rec)}
              onDetails={() => setDetail(rec)}
            />
          ))}
        </div>
      )}

      {detail && (
        <RequestDetailModal
          record={detail}
          model={model}
          onClose={() => setDetail(null)}
          onOpenInPlayground={() => {
            onOpenInPlayground(detail);
            setDetail(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows / cards
// ---------------------------------------------------------------------------

function RequestRow({
  record,
  compact,
  showPreview,
  onOpenInPlayground,
  onDetails,
}: {
  record: ApiCallRecord;
  compact: boolean;
  showPreview: boolean;
  onOpenInPlayground: () => void;
  onDetails: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{timeAgo(record.created_at)}</span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-500 font-mono">{formatDuration(record.duration_seconds)}</span>
          <span className="text-slate-300">·</span>
          <StatusBadge status={record.status} />
          <RetriesBadge record={record} />
          <SafetyRetryBadge record={record} />
          <SourceBadge record={record} />
          <DummyBadge record={record} />
          <EndpointBadge provider={record.provider} modelId={record.model_id} />
        </div>

        <div className="mt-1 flex items-center gap-2">
          <code className="text-xs text-slate-400 font-mono truncate">{record.id}</code>
          <InlineCopyButton value={record.id} title="Copy request ID" />
        </div>

        {!compact && (
          <p className="mt-2 text-sm text-slate-700 line-clamp-2">
            {record.prompt || <span className="text-slate-400 italic">No prompt</span>}
          </p>
        )}

        {!compact && <ParamTags record={record} />}

        {record.status === 'error' && record.error && (
          <p className="mt-2 text-xs text-danger line-clamp-2">{record.error}</p>
        )}

        {!compact && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <ActionButton onClick={onDetails} icon="search">
              Details
            </ActionButton>
            <ActionButton onClick={onOpenInPlayground} icon="play">
              Playground
            </ActionButton>
            {record.prompt && (
              <CopyActionButton value={record.prompt} icon="copy">
                Copy prompt
              </CopyActionButton>
            )}
            {record.output_ref && (
              <a
                href={record.output_ref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open output
              </a>
            )}
          </div>
        )}
      </div>

      {showPreview && (
        <button
          type="button"
          onClick={onDetails}
          title="Open request details"
          className="shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <Preview record={record} className={compact ? 'h-14 w-20' : 'h-24 w-32'} />
        </button>
      )}
    </div>
  );
}

function RequestGridCard({
  record,
  showPreview,
  onOpenInPlayground,
  onDetails,
}: {
  record: ApiCallRecord;
  showPreview: boolean;
  onOpenInPlayground: () => void;
  onDetails: () => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden flex flex-col">
      {showPreview && (
        <button type="button" onClick={onDetails} title="Open request details" className="block">
          <Preview record={record} className="h-40 w-full rounded-none border-0 border-b border-slate-100" />
        </button>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-900">{timeAgo(record.created_at)}</span>
          <StatusBadge status={record.status} />
          <RetriesBadge record={record} />
          <SafetyRetryBadge record={record} />
          <SourceBadge record={record} />
          <DummyBadge record={record} />
        </div>
        <div className="mt-1.5">
          <EndpointBadge provider={record.provider} modelId={record.model_id} className="max-w-full" />
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <code className="text-[11px] text-slate-400 font-mono truncate">{record.id}</code>
          <InlineCopyButton value={record.id} title="Copy request ID" />
        </div>
        <p className="mt-2 text-sm text-slate-700 line-clamp-3 flex-1">
          {record.prompt || <span className="text-slate-400 italic">No prompt</span>}
        </p>
        {record.status === 'error' && record.error && (
          <p className="mt-2 text-xs text-danger line-clamp-2">{record.error}</p>
        )}
        <ParamTags record={record} />
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <ActionButton onClick={onDetails} icon="search">
            Details
          </ActionButton>
          <ActionButton onClick={onOpenInPlayground} icon="play">
            Playground
          </ActionButton>
          {record.prompt && (
            <CopyActionButton value={record.prompt} icon="copy">
              Copy prompt
            </CopyActionButton>
          )}
        </div>
      </div>
    </div>
  );
}

function Preview({ record, className }: { record: ApiCallRecord; className: string }) {
  const [failed, setFailed] = useState(false);
  const src = record.output_ref;
  if (!src || failed) {
    return (
      <div
        className={`${className} shrink-0 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt="Output preview"
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} shrink-0 rounded-lg border border-slate-200 object-cover bg-slate-50`}
    />
  );
}

function ParamTags({ record }: { record: ApiCallRecord }) {
  const tags: [string, string][] = [];
  if (record.params && typeof record.params === 'object') {
    for (const [k, v] of Object.entries(record.params)) {
      if (v == null || v === '') continue;
      tags.push([k, String(v)]);
    }
  }
  if (record.seed != null) tags.push(['seed', String(record.seed)]);
  if (tags.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {tags.map(([k, v]) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-mono text-slate-500"
        >
          {k} <span className="font-semibold text-slate-700">{v}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Marks a call that did not come from an external client: the Playground, or a QA
 * load test. Ordinary API traffic is the norm here and stays bare.
 */
function SourceBadge({ record }: { record: ApiCallRecord }) {
  const source = sourceOf(record);
  if (source === 'api') return null;
  return (
    <SharedSourceBadge
      source={source}
      baseClassName="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold"
    />
  );
}

/**
 * How many transient provider failures this call rode out before it settled.
 * Absent when nothing was retried, and on rows recorded before retry tracking
 * (where the count is unknown rather than zero).
 */
function RetriesBadge({ record }: { record: ApiCallRecord }) {
  const retries = record.retries;
  if (retries == null || retries <= 0) return null;
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700"
      title={`The provider failed ${retries} time${retries === 1 ? '' : 's'} with a transient error and was retried`}
    >
      {retries} {retries === 1 ? 'retry' : 'retries'}
    </span>
  );
}

/**
 * Marks a call the provider's policy filter rejected outright, which only
 * succeeded once a system instruction was attached. The prompt itself was sent
 * unchanged. Absent on every ordinary call.
 */
function SafetyRetryBadge({ record }: { record: ApiCallRecord }) {
  if (!record.retry_system_instruction) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold bg-orange-100 text-orange-700"
      title="The provider's policy filter rejected this input outright. The same prompt was retried with a system instruction attached, which produced this output — see Details."
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
      Safety retry
    </span>
  );
}

/** Marks a call that was served by the dummy provider, so it cost nothing. */
function DummyBadge({ record }: { record: ApiCallRecord }) {
  if (!record.dummy) return null;
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold bg-slate-200 text-slate-600"
      title="Dummy call: no provider was reached, so the output is noise and the cost is zero"
    >
      Dummy
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === 'success';
  const pending = isInProgress(status);
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold font-mono ${
        ok
          ? 'bg-success-100 text-success-700'
          : pending
            ? 'bg-slate-100 text-slate-500'
            : 'bg-danger-100 text-danger-700'
      }`}
      title={ok ? 'HTTP 200 · success' : pending ? `Still ${status}` : 'Provider error'}
    >
      {ok ? '200' : pending ? status : 'error'}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Small controls
// ---------------------------------------------------------------------------

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-indigo-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  const items: [ViewMode, string, ReactNode][] = [
    [
      'compact',
      'Compact list',
      <path key="p" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 10h16M4 14h16M4 18h16" />,
    ],
    [
      'comfortable',
      'Comfortable list',
      <path key="p" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h16" />,
    ],
    [
      'grid',
      'Grid',
      <path key="p" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />,
    ],
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
      {items.map(([value, label, icon], idx) => (
        <button
          key={value}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={view === value}
          onClick={() => onChange(value)}
          className={`p-1.5 transition-colors ${idx > 0 ? 'border-l border-slate-200' : ''} ${
            view === value ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-slate-400 hover:bg-slate-50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon}
          </svg>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  return `${seconds.toFixed(2)}s`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return `${secs} second${secs === 1 ? '' : 's'} ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// ---------------------------------------------------------------------------
// Request detail (debugging)
// ---------------------------------------------------------------------------

/**
 * Build a request body that reproduces a recorded call, reusing the stored
 * input image URLs (the backend accepts http(s) URLs for image/mask inputs).
 * Endpoint-specific fields come from the model spec so the shape matches what
 * the endpoint expects.
 */
function buildReproBody(
  record: ApiCallRecord,
  model: ApiEndpointModel,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const metaKeys = ['scan_id', 'organization_id', 'job_id', 'view', 'player', 'dvp'] as const;
  for (const k of metaKeys) {
    const v = record[k];
    if (v) body[k] = v;
  }
  const refs = record.input_refs || {};
  for (const spec of model.inputs) {
    if (spec.type === 'image' || spec.type === 'mask') {
      if (refs[spec.name]) body[spec.name] = refs[spec.name];
    } else if (spec.type === 'images') {
      const arr = Object.keys(refs)
        .filter((k) => k.startsWith('reference_'))
        .sort()
        .map((k) => refs[k]);
      if (arr.length) body[spec.name] = arr;
    } else if (spec.name === 'prompt') {
      if (record.prompt) body.prompt = record.prompt;
    } else if (spec.name === 'seed') {
      if (record.seed != null) body.seed = record.seed;
    } else {
      const params = record.params || {};
      const v = params[spec.name];
      if (v != null && v !== '') body[spec.name] = v;
    }
  }
  return body;
}

function RequestDetailModal({
  record,
  model,
  onClose,
  onOpenInPlayground,
}: {
  record: ApiCallRecord;
  model: ApiEndpointModel;
  onClose: () => void;
  onOpenInPlayground: () => void;
}) {
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // While the lightbox is open, let it handle Escape (close image first).
      if (e.key === 'Escape' && !lightbox) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, lightbox]);

  const inputRefs = record.input_refs || {};
  const inputEntries = Object.entries(inputRefs);
  const reproBody = buildReproBody(record, model);
  const params = record.params || {};
  const paramEntries = Object.entries(params).filter(([, v]) => v != null && v !== '');

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-white/95 backdrop-blur">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">{model.label}</h3>
            <StatusBadge status={record.status} />
            <span className="text-xs text-slate-400">{timeAgo(record.created_at)}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Error (front and center when the call failed) */}
          {record.status === 'error' && record.error && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
              <div className="text-xs font-semibold text-danger mb-1">Error</div>
              <pre className="text-xs text-danger whitespace-pre-wrap break-words">{record.error}</pre>
            </div>
          )}

          {/* Images: inputs + output */}
          <section>
            <SectionTitle>Images</SectionTitle>
            <div className="flex flex-wrap gap-3">
              {inputEntries.map(([name, url]) => (
                <ImageTile key={name} label={name} url={url} onExpand={setLightbox} />
              ))}
              <ImageTile label="output" url={record.output_ref} highlight onExpand={setLightbox} />
              {inputEntries.length === 0 && !record.output_ref && (
                <p className="text-xs text-slate-400">No stored images for this request.</p>
              )}
            </div>
          </section>

          {/* Metadata + accounting */}
          <section>
            <SectionTitle>Metadata</SectionTitle>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <Field label="Request ID" value={record.id} mono copy />
              <Field label="Created" value={formatTimestamp(record.created_at)} />
              <Field label="Provider" value={record.provider} />
              <Field label="Model ID" value={record.model_id} mono />
              <Field label="Scan ID" value={record.scan_id} mono copy />
              <Field label="Organization ID" value={record.organization_id} mono copy />
              <Field label="Job ID" value={record.job_id} mono copy />
              <Field label="View" value={record.view} />
              <Field label="Player" value={record.player} copy />
              <Field label="DVP" value={record.dvp} />
              <Field label="Source" value={SOURCE_LABELS[sourceOf(record)]} />
              <Field
                label="Cost"
                value={record.cost_usd != null ? `$${record.cost_usd.toFixed(5)}` : null}
              />
              <Field label="Duration" value={formatDuration(record.duration_seconds)} />
              <Field
                label="Retries"
                value={record.retries != null ? String(record.retries) : null}
              />
              {record.dummy && <Field label="Dummy" value="Yes (no provider reached)" />}
            </dl>
          </section>

          {/* Prompt */}
          {record.prompt && (
            <section>
              <SectionTitle>
                {record.retry_system_instruction ? 'Prompt (sent unchanged)' : 'Prompt'}
              </SectionTitle>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-lg border border-slate-200 p-3">
                {record.prompt}
              </pre>
            </section>
          )}

          {/* The instruction that salvaged the call, shown only when one was needed. */}
          {record.retry_system_instruction && (
            <section>
              <SectionTitle>Safety retry — added system instruction</SectionTitle>
              <p className="mb-2 text-xs text-orange-700">
                The provider's policy filter rejected this input outright and returned no
                image. The prompt above was then retried unchanged with the instruction
                below attached, which is what produced this output.
              </p>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-orange-50 rounded-lg border border-orange-200 p-3">
                {record.retry_system_instruction}
              </pre>
            </section>
          )}

          {/* Params */}
          {(paramEntries.length > 0 || record.seed != null) && (
            <section>
              <SectionTitle>Params</SectionTitle>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {record.seed != null && <Field label="seed" value={String(record.seed)} mono />}
                {paramEntries.map(([k, v]) => (
                  <Field key={k} label={k} value={String(v)} mono />
                ))}
              </dl>
            </section>
          )}

          {/* Reproduce */}
          <section>
            <SectionTitle>Reproduce</SectionTitle>
            <div className="flex flex-wrap items-center gap-2">
              <ActionButton onClick={onOpenInPlayground} icon="play">
                Open in Playground
              </ActionButton>
              <CopyActionButton value={JSON.stringify(record, null, 2)} icon="copy">
                Copy record JSON
              </CopyActionButton>
              <CopyActionButton value={JSON.stringify(reproBody, null, 2)} icon="copy">
                Copy request body
              </CopyActionButton>
              <CopyActionButton value={buildCurl(model.path, reproBody)} icon="copy">
                Copy cURL
              </CopyActionButton>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              The request body and cURL reuse the stored input image URLs, so they re-run the exact
              same inputs.
            </p>
          </section>
        </div>
      </div>
    </div>
    {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{children}</h4>;
}

function Field({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-50 py-0.5">
      <dt className="text-xs text-slate-400 shrink-0">{label}</dt>
      <dd className={`text-xs text-slate-700 text-right break-all ${mono ? 'font-mono' : ''}`}>
        {value ? (
          <span className="inline-flex items-center gap-1.5">
            {value}
            {copy && <InlineCopyButton value={value} title={`Copy ${label}`} />}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </dd>
    </div>
  );
}

function ImageTile({
  label,
  url,
  highlight,
  onExpand,
}: {
  label: string;
  url: string | null;
  highlight?: boolean;
  onExpand: (url: string) => void;
}) {
  const [failed, setFailed] = useState(false);
  const canExpand = Boolean(url) && !failed;
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => url && onExpand(url)}
        disabled={!canExpand}
        className={`block h-28 w-28 rounded-lg border overflow-hidden bg-slate-900 ${
          canExpand ? 'cursor-zoom-in' : 'cursor-default'
        } ${highlight ? 'border-indigo-300' : 'border-slate-200'}`}
        title={canExpand ? 'Click to expand' : undefined}
      >
        {url && !failed ? (
          <img
            src={url}
            alt={label}
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-500 text-[11px]">
            {url ? 'failed to load' : 'none'}
          </div>
        )}
      </button>
      <span className="text-[11px] font-mono text-slate-500">{label}</span>
    </div>
  );
}
